import axios from "axios";
import { message } from "antd";

console.log(
  "process.env.REACT_APP_API_BASE_URL",
  process.env.REACT_APP_API_BASE_URL
);

// 创建axios实例，统一配置请求根路径、超时等
const service = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL, // 环境变量配置不变，兼容代理
  timeout: 15000, // 请求超时时间 15秒
  withCredentials: true, // 允许跨域携带cookie
  headers: {
    "Content-Type": "application/json;charset=utf-8", // 默认请求头
  },
});

// 存储正在请求的接口，用于【取消重复请求】
const pendingMap = new Map();

/**
 * 生成每个请求的唯一标识
 * @param {Object} config 请求配置
 */
const getPendingKey = (config) => {
  let { url, method, params, data } = config;
  if (typeof data === "string") data = JSON.parse(data);
  return [url, method, JSON.stringify(params), JSON.stringify(data)].join("&");
};

/**
 * 添加重复请求到pendingMap
 */
const addPending = (config) => {
  const key = getPendingKey(config);
  config.cancelToken =
    config.cancelToken ||
    new axios.CancelToken((cancel) => {
      if (!pendingMap.has(key)) {
        pendingMap.set(key, cancel);
      }
    });
};

/**
 * 移除重复请求
 */
const removePending = (config) => {
  const key = getPendingKey(config);
  if (pendingMap.has(key)) {
    const cancel = pendingMap.get(key);
    cancel("请勿重复请求"); // 取消重复的请求
    pendingMap.delete(key);
  }
};

// ------------------- 请求拦截器：请求发送前执行 -------------------
service.interceptors.request.use(
  (config) => {
    // removePending(config); // 你注释的代码保留原样
    addPending(config); // 添加当前请求到pendingMap

    // 1. 携带token：从本地存储中获取token，请求头里带上【项目必备】
    // const token =
    //   localStorage.getItem("token") || sessionStorage.getItem("token");
    // if (token) {
    //   config.headers.Authorization = `Bearer ${token}`;
    // }
    // 从本地缓存获取i18n的当前语言，和你的项目多语言完全同步
    const currentLang = localStorage.getItem("i18nextLng") || "zh";
    config.headers.language = currentLang;
    // 2. 自定义配置：比如某些请求需要form-data格式，单独传配置即可
    if (config.isFormData) {
      config.headers["Content-Type"] = "multipart/form-data";
    }

    return config;
  },
  (error) => {
    // 请求出错时的处理
    return Promise.reject(error);
  }
);

// ------------------- 响应拦截器：接口返回后执行 -------------------
service.interceptors.response.use(
  (response) => {
    removePending(response.config); // 请求成功，移除pending中的请求
    const res = response.data;

    // ✅【核心修改1】适配后端最常见的2种返回格式，兼容所有情况
    // 情况1：后端返回 { code:200/0, data:xxx, msg:xxx } 标准格式
    if (res.code === 200 || res.code === 0) {
      return res.data ?? res; // 成功：直接返回纯净数据，业务层无需再res.data
    }
    // 情况2：后端接口直接返回数据（无code），比如文件流/纯对象/数组，直接放行
    else if (typeof res === "object" && !res.code) {
      return res;
    }
    // 业务逻辑失败
    else {
      message.error(res.msg || "操作失败");
      return Promise.reject(new Error(res.msg || "操作失败"));
    }
  },
  (error) => {
    removePending(error.config || {}); // 报错也移除pending
    // 统一处理所有请求错误：网络错误、超时、401、403、500等
    let errMsg = "";
    if (axios.isCancel(error)) {
      // 取消重复请求的错误，不提示，直接返回reject
      return Promise.reject(error);
    } else if (error.message.includes("timeout")) {
      errMsg = "请求超时，请稍后重试";
    } else if (error.message.includes("Network Error")) {
      errMsg = "网络连接异常，请检查网络";
    } else {
      // HTTP状态码错误
      switch (error.response?.status) {
        case 401:
          errMsg = "登录失效，请重新登录";
          localStorage.removeItem("token"); // 清除过期token
          window.location.href = "/login"; // 跳转到登录页
          break;
        case 403:
          errMsg = "暂无权限访问该资源";
          break;
        case 404:
          errMsg = "接口地址不存在";
          break;
        case 500:
          errMsg = "服务器内部错误，请稍后重试";
          break;
        default:
          errMsg = error.response?.data?.msg || "请求失败，请稍后重试";
      }
    }
    // 错误提示
    message.error(errMsg);
    return Promise.reject(error);
  }
);

// ------------------- ✅【核心修改2】极简封装GET/POST/PUT/DELETE，完美适配你的调用方式 -------------------
// 这种封装方式：return service()  刚好匹配你 接口文件return get/post() 的写法
export const get = (url, params = {}) => {
  return service({ url, method: "get", params });
};

export const post = (url, data = {}, config = {}) => {
  return service({ url, method: "post", data, ...config });
};

export const put = (url, data = {}) => {
  return service({ url, method: "put", data });
};

export const del = (url, params = {}) => {
  return service({ url, method: "delete", params });
};

// 默认导出axios实例（特殊场景使用）
export default service;
